import { isNumber, isString } from 'class-validator';

export const SELF_CHECKIN_RADIUS_LIMIT_MILES = 1;

export const GATE_MANAGEMENT_SALES_URL =
  'https://lp.opendock.com/gate-management?utm_source=opendockapp&utm_medium=cta&utm_campaign=od-gate-management-warehouse-opendock-q4-24&utm_content=appt-details';

export const GATE_MANAGEMENT_USER_EMAIL = 'gatemanagement@example.com';

export const SELF_CHECKIN_LEAD_TIME_MINUTES = 60 * 24;

export const SMS_COUNTRIES: readonly string[] = ['USA', 'CAN'];

// Warehouses with no country set are treated as USA, matching how check-in SMS has always
// resolved an unset country.
export function isSmsCheckInCountry(warehouse?: { country?: string | null } | null): boolean {
  return SMS_COUNTRIES.includes(warehouse?.country ?? 'USA');
}

export function milesToMeters(miles: number): number {
  if (isNumber(miles) || isString(miles)) {
    return Number(miles) * 1609.34;
  }
  throw new Error(`Invalid miles ${miles}`);
}

export enum GeofenceRadiusUnit {
  MILES = 'mi',
  KILOMETERS = 'km'
}

export const DEFAULT_GEOFENCE_RADIUS_UNIT = GeofenceRadiusUnit.MILES;

export function geofenceRadiusToMeters(radius: number, unit: GeofenceRadiusUnit): number {
  if (unit === GeofenceRadiusUnit.KILOMETERS) {
    return Number(radius) * 1000;
  }
  return milesToMeters(radius);
}

/** "1 mile", "2 miles", "5 kilometers" - the wording drivers see in the out-of-range error. */
export function formatGeofenceRadius(radius: number, unit: GeofenceRadiusUnit): string {
  const noun = unit === GeofenceRadiusUnit.KILOMETERS ? 'kilometer' : 'mile';
  return `${radius} ${noun}${radius >= 2 ? 's' : ''}`;
}

/**
 * The radius a warehouse enforces, in the unit it was configured in. A missing or zero radius
 * falls back to the 1-mile default (in miles, whatever the stored unit), as it always has.
 */
export function resolveGeofenceRadius(settings?: {
  gateManagementGeofenceRadius?: number | null;
  gateManagementGeofenceRadiusUnit?: GeofenceRadiusUnit | null;
}): { radius: number; unit: GeofenceRadiusUnit } {
  const radius = Number(settings?.gateManagementGeofenceRadius);
  if (!radius) {
    return { radius: SELF_CHECKIN_RADIUS_LIMIT_MILES, unit: GeofenceRadiusUnit.MILES };
  }
  return {
    radius,
    unit: settings?.gateManagementGeofenceRadiusUnit ?? DEFAULT_GEOFENCE_RADIUS_UNIT
  };
}
export interface ICheckInPrimaryField {
  label: string;
  key: string;
  type: string;
  description: string;
  requiredForDrivers: boolean;
  requiredForWarehouse: boolean;
}

export const CheckInPrimaryFields: ICheckInPrimaryField[] = [
  {
    label: 'Asset Type',
    key: 'assetType',
    type: 'singleSelect',
    description: '',
    requiredForDrivers: true,
    requiredForWarehouse: true
  },
  {
    label: 'Asset ID',
    key: 'assetId',
    type: 'number',
    description: '',
    requiredForDrivers: true,
    requiredForWarehouse: true
  },
  {
    label: "Driver's Phone Number",
    key: 'driversPhoneNumber',
    type: 'number',
    description: '',
    requiredForDrivers: true,
    requiredForWarehouse: true
  },
  {
    label: 'Carrier Company',
    key: 'carrierCompany',
    type: 'singleSelect',
    description: '',
    requiredForDrivers: true,
    requiredForWarehouse: true
  },
  {
    label: 'Reference Number',
    key: 'referenceNumber',
    type: 'text',
    description: '',
    requiredForDrivers: false,
    requiredForWarehouse: false
  },
  {
    label: 'License Plate',
    key: 'licensePlate',
    type: 'text',
    description: '',
    requiredForDrivers: false,
    requiredForWarehouse: false
  },
  {
    label: 'DOT Number',
    key: 'dotNumber',
    type: 'text',
    description: '',
    requiredForDrivers: false,
    requiredForWarehouse: false
  },
  {
    label: 'Driver Notes',
    key: 'driverNotes',
    type: 'text',
    description: '',
    requiredForDrivers: false,
    requiredForWarehouse: false
  }
];
